import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import { Mic, Square } from 'lucide-react'
import { useWavRecorder } from '@/components/useWavRecorder'
import {
  recordVoiceNote,
  discardVoiceNote,
  fetchVoiceNoteAudio,
  deleteVoiceRecording,
  draftHeading,
  factRecordingLine,
  formatLength,
  LOW_CONFIDENCE_LINE,
  DELETE_RECORDING_CONFIRM,
  VoiceNoteError,
  type VoiceNoteDraft,
  type FactRecording,
} from '@/lib/voice-notes'
import { micFailureMessage } from '@/lib/microphone'

/**
 * ANSWER BY VOICE ON THE QUESTIONS PAGE (Build 10b, Kelvo).
 *
 * The owner presses "Answer by voice", talks, presses Stop. The recording goes
 * to the engine, comes back as a DRAFT, and the card puts the words in his
 * answer box. He reads them — playing the recording back if he wants — fixes
 * what the machine misheard, and presses the card's ordinary Answer button.
 * Approval, not transcription, is what creates the fact: this component never
 * saves an answer, and has no button that could.
 *
 * Every word it shows lives in src/lib/voice-notes.ts, where it is tested.
 *
 * THE MICROPHONE. The Kelvo dashboard's Permissions-Policy grants it to its
 * own origin (scripts/kelvo-headers-policy.mjs); without that, start() fails
 * and he is told how to allow it, never shown a button that silently does
 * nothing.
 */

/** The recorder's own ceiling; the engine refuses past two minutes. */
const MAX_SECONDS = 90

const BUTTON =
  'rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2'

export function VoiceAnswerPanel({
  questionId,
  draft,
  onDraft,
  onDiscarded,
  onStale,
  disabled,
}: {
  questionId: string
  /** The draft waiting on this question, if any. */
  draft: VoiceNoteDraft | null
  /** A new draft came back: the card puts its words in the answer box. */
  onDraft: (draft: VoiceNoteDraft) => void
  /** The draft was thrown away. */
  onDiscarded: () => void
  /** The page is out of date (a draft made in another tab): reload the list. */
  onStale: () => void
  /** The card is saving something else. */
  disabled: boolean
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [phase, setPhase] = useState<'idle' | 'starting' | 'sending' | 'discarding'>('idle')
  const [error, setError] = useState('')

  const recorder = useWavRecorder({
    maxSeconds: MAX_SECONDS,
    onFinished: (wav) => void send(wav),
  })

  async function start() {
    setError('')
    setPhase('starting')
    const failure = await recorder.start()
    setPhase('idle')
    if (failure) setError(micFailureMessage(failure))
  }

  async function send(wav: ArrayBuffer) {
    setPhase('sending')
    setError('')
    try {
      const made = await recordVoiceNote(
        await getTokenRef.current(),
        questionId,
        new Blob([wav], { type: 'audio/wav' }),
      )
      onDraft(made)
    } catch (e) {
      if (e instanceof VoiceNoteError && e.code === 'draft_exists') onStale()
      setError(e instanceof VoiceNoteError ? e.message : 'That recording did not save. Try again, or type your answer.')
    } finally {
      setPhase('idle')
    }
  }

  async function discard() {
    if (!draft) return
    setPhase('discarding')
    setError('')
    try {
      await discardVoiceNote(await getTokenRef.current(), draft.id)
      onDiscarded()
    } catch (e) {
      setError(e instanceof VoiceNoteError ? e.message : 'Could not discard the draft. Try again.')
    } finally {
      setPhase('idle')
    }
  }

  const busy = disabled || phase !== 'idle'

  if (draft) {
    return (
      <div className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3">
        <p className="text-sm text-text-primary">{draftHeading(draft)}</p>
        {draft.lowConfidence ? <p className="mt-1 text-sm font-medium text-text-danger">{LOW_CONFIDENCE_LINE}</p> : null}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <PlayRecording noteId={draft.id} disabled={busy} />
          <button onClick={() => void discard()} disabled={busy} className={BUTTON}>
            {phase === 'discarding' ? 'Discarding…' : 'Discard and record again'}
          </button>
        </div>
        {error ? <p className="mt-2 text-sm text-text-danger">{error}</p> : null}
      </div>
    )
  }

  return (
    <div className="mt-3">
      {recorder.recording ? (
        <div className="flex flex-wrap items-center gap-2" aria-live="polite">
          <button
            onClick={() => recorder.stop()}
            className="inline-flex items-center gap-2 rounded-lg bg-text-danger px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            <Square className="h-4 w-4" aria-hidden="true" />
            Stop ({formatLength(recorder.seconds)})
          </button>
          <button onClick={() => recorder.cancel()} className={BUTTON}>
            Cancel
          </button>
          <span className="text-sm text-text-muted">Recording. Up to {formatLength(MAX_SECONDS)}.</span>
        </div>
      ) : phase === 'sending' ? (
        <p className="text-sm text-text-muted" role="status">
          Writing that up… this takes a few seconds.
        </p>
      ) : (
        <button onClick={() => void start()} disabled={busy} className={`inline-flex items-center gap-2 ${BUTTON}`}>
          <Mic className="h-4 w-4" aria-hidden="true" />
          {phase === 'starting' ? 'Starting…' : 'Answer by voice'}
        </button>
      )}
      {error ? <p className="mt-2 text-sm text-text-danger">{error}</p> : null}
    </div>
  )
}

/**
 * Play a recording: fetched with the owner's session (an <audio src> cannot
 * carry it), held as a blob URL for as long as the button is on screen, and
 * released when it goes.
 */
function PlayRecording({ noteId, disabled, label = 'Play it back' }: { noteId: string; disabled: boolean; label?: string }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const blob = await fetchVoiceNoteAudio(await getTokenRef.current(), noteId)
      setUrl(URL.createObjectURL(blob))
    } catch (e) {
      setError(e instanceof VoiceNoteError ? e.message : 'Could not play the recording. Try again.')
    } finally {
      setLoading(false)
    }
  }

  if (url) {
    return <audio controls autoPlay src={url} className="h-9 max-w-full" aria-label="The recording" />
  }
  return (
    <>
      <button onClick={() => void load()} disabled={disabled || loading} className={BUTTON}>
        {loading ? 'Loading…' : label}
      </button>
      {error ? <span className="text-sm text-text-danger">{error}</span> : null}
    </>
  )
}

/**
 * The recording behind an approved answer, on the Facts page: play it, or
 * delete it (the answer stays). Owner only, like the page.
 */
export function FactRecordingLine({ recording, onChanged }: { recording: FactRecording; onChanged: () => void }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function remove() {
    setBusy(true)
    setError('')
    try {
      await deleteVoiceRecording(await getTokenRef.current(), recording.id)
      onChanged()
    } catch (e) {
      setError(e instanceof VoiceNoteError ? e.message : 'Could not delete the recording. Try again.')
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <div className="mt-2">
      <p className="text-xs text-text-muted">{factRecordingLine(recording)}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <PlayRecording noteId={recording.id} disabled={busy} label="Play the recording" />
        {confirming ? (
          <>
            <span className="text-sm text-text-primary">{DELETE_RECORDING_CONFIRM}</span>
            <button onClick={() => void remove()} disabled={busy} className={BUTTON}>
              {busy ? 'Deleting…' : 'Yes, delete it'}
            </button>
            <button onClick={() => setConfirming(false)} disabled={busy} className="text-sm text-text-muted underline underline-offset-2">
              Keep it
            </button>
          </>
        ) : (
          <button onClick={() => setConfirming(true)} disabled={busy} className="text-sm text-text-muted underline underline-offset-2">
            Delete the recording
          </button>
        )}
      </div>
      {error ? <p className="mt-1 text-sm text-text-danger">{error}</p> : null}
    </div>
  )
}
